import { customBreakdownFieldAt } from 'features/charts-v2/components/breakdown-selection-utils'
import type { ChartData } from 'features/charts-v2/types/chart-strategy'

/**
 * Maps raw backend category values to human-readable display labels for specific breakdown fields.
 *
 * Display-only: these labels must NOT be sent back to the backend as filter values.
 * The backend stores the raw values ("True"/"False"); the mapping is purely for rendering.
 *
 * The breakdown field key comes from the backend custom-field name verbatim (e.g. "Verified",
 * "Recommended") — compare case-insensitively so both capitalizations are handled safely.
 * Raw category values ("True"/"False") are also matched case-insensitively.
 */
const BOOLEAN_VALUE_LABELS: Record<
  string,
  { trueLabel: string; falseLabel: string }
> = {
  verified: { trueLabel: 'Verified', falseLabel: 'Not Verified' },
  recommended: { trueLabel: 'Recommended', falseLabel: 'Not Recommended' },
}

/**
 * Returns the display label for a raw category value given the active breakdown field.
 * Falls through to the raw value for unknown fields or unrecognized values.
 */
export function formatBreakdownCategoryValue(
  breakdownField: string | undefined,
  rawValue: string,
): string {
  if (!breakdownField) return rawValue

  const mapping = BOOLEAN_VALUE_LABELS[breakdownField.toLowerCase()]
  if (!mapping) return rawValue

  const lower = rawValue.toLowerCase()
  if (lower === 'true') return mapping.trueLabel
  if (lower === 'false') return mapping.falseLabel
  return rawValue
}

/**
 * Inverse of formatBreakdownCategoryValue: converts a display label back to the raw backend
 * value so filter/constraint round-trips reach the backend with the value it actually stores.
 *
 * "Verified" → "True", "Not Recommended" → "False", anything else passes through unchanged.
 */
export function unformatBreakdownCategoryValue(
  breakdownField: string | undefined,
  displayValue: string,
): string {
  if (!breakdownField) return displayValue

  const mapping = BOOLEAN_VALUE_LABELS[breakdownField.toLowerCase()]
  if (!mapping) return displayValue

  if (displayValue === mapping.trueLabel) return 'True'
  if (displayValue === mapping.falseLabel) return 'False'
  return displayValue
}

/**
 * Returns a shallow copy of ChartData with boolean category values aliased for display,
 * per breakdown position.  category[i] is formatted only when selectedBreakdowns[i] is a
 * custom boolean breakdown (e.g. "verified", "recommended"); all other values pass through
 * unchanged, including null (which transforms later normalise to ALIAS_FOR_NULL_VALUES).
 *
 * Apply this once — at the top of the render pipeline — so every chart type, legend, and
 * data table sees consistent display labels without per-strategy aliasing boilerplate.
 */
export function aliasChartDataForDisplay(
  data: ChartData,
  selectedBreakdowns: readonly unknown[],
): ChartData {
  const aliasItem = (
    item: ChartData['data'][number],
  ): ChartData['data'][number] => ({
    ...item,
    category: item.category.map((val, i) => {
      const field = customBreakdownFieldAt(selectedBreakdowns, i)
      if (!field || val == null) return val
      return formatBreakdownCategoryValue(field, String(val))
    }),
  })

  return {
    ...data,
    data: data.data.map(aliasItem),
    parent_data:
      data.parent_data == null
        ? data.parent_data
        : data.parent_data.map(aliasItem),
  }
}
